import { FC, Fragment, useMemo, useRef } from 'react'

import { Media } from '@syconium/little-miss-figgy'

import { RenderOnceInViewport } from '../../../../app/_components/performance/RenderOnceInViewport.client'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { FitModelCaption } from '../../../../brunswick/components/fit-model/FitModelCaption'
import { StickyParent } from '../../../../brunswick/components/fit-model/FitModelCaption/styles'
import { NostoProductRecommendationsSection } from '../../../../brunswick/components/NostoProductRecommendationsSection'
import { ReviewSection, ReviewsBody } from '../../../../brunswick/components/Reviews'
import { SliderFitModelWrapper } from '../../../../brunswick/components/SwiperImageSlider/styles'
import { outerLayerGarmentCategoryKeys } from '../../../../constants/figs'
import { getProductGalleryImages } from '../../../../lib/utils'
import { ProductAsset, Variant } from '../../../../types/graphql'
import { PageSection } from '../../../page-sections/PageSection'
import { ProductPageBreadcrumbs } from '../Breadcrumbs/ProductPageBreadcrumbs'
import * as ProductPageStyles from '../styles'

import { FitModelStateContainer } from './contexts/FitModelStateContainer'
import { GalleryImageStateContainer } from './contexts/GalleryImageStateContainer'
import { DetailsSection } from './DetailsSection/DetailsSection'
import { FigsScrubsAreSection } from './FigsScrubsAreSection/FigsScrubsAreSection'
import { UseFetchProductPageDataOutput } from './hooks/useFetchProductPageData'
import { isGiftCardHandle, useIsGiftCard } from './hooks/useIsGiftCard'
import { ImageZoom } from './ImageZoom'
import { usePdpServerSideProps } from './PdpServerSidePropsProvider'
import { ProductAssetsSection } from './ProductAssetsSection'
import { ProductPageSchemaTags as ProductPageSchemaTagsImpl } from './ProductPageSchemaTags'
import { ProductPageTrackingEffects } from './ProductPageTrackingEffects'
import { ShopTheLookSection } from './ShopTheLookSection/ShopTheLookSection'
import { ReviewsPlaceholder } from './styles'
import { WhyWeLove } from './WhyWeLoveSection/WhyWeLoveSection'

/**
 * @deprecated Delete this and just use usePdpServerSideProps to get initial data rather than passing these.
 */
export interface ProductPageTemplateProps {
  productGroup: Exclude<UseFetchProductPageDataOutput['productGroup'], null | undefined>
  productGroupStatus: Exclude<
    UseFetchProductPageDataOutput['productGroupStatus'],
    'rejected' | 'idle'
  >
  featuredProduct: Exclude<UseFetchProductPageDataOutput['featuredProduct'], null | undefined>
  featuredProductStatus: Exclude<
    UseFetchProductPageDataOutput['featuredProductStatus'],
    'rejected' | 'idle'
  >
  selectedVariant: Variant | undefined
}

const ProductPageSchemaTags = () => {
  const { data } = usePdpServerSideProps()

  return data?.product.variantGroup.variant ? (
    <ProductPageSchemaTagsImpl
      product={data.product}
      variantGroup={data.product.variantGroup}
      variant={data.product.variantGroup.variant}
    />
  ) : null
}

const ProductPageProductGallery = (props: ProductPageTemplateProps) => {
  const { productGroup, featuredProduct } = props
  const { fitModel, setFitModel } = FitModelStateContainer.useContainer()

  const iconBadge = featuredProduct.iconBadge
  const iconBadgePlacement = featuredProduct.iconBadgePlacement ?? productGroup.iconBadgePlacement

  const previousFeaturedProductRef = useRef(featuredProduct)
  if (previousFeaturedProductRef.current !== featuredProduct) {
    previousFeaturedProductRef.current = featuredProduct
    setFitModel(featuredProduct.fitModel ?? undefined)
  }

  const images: readonly ProductAsset[] = useMemo(() => {
    if (!featuredProduct.images?.nodes) return []
    return getProductGalleryImages(featuredProduct.images.nodes)
  }, [featuredProduct])
  const videos = useMemo(() => {
    return featuredProduct?.videos.nodes.filter(
      video => video.kind === 'ugc' || video.kind === 'catwalk' || video.kind === 'other'
    )
  }, [featuredProduct?.videos.nodes])

  const showVideoCta = useMemo(() => {
    return (
      videos.findIndex(video => {
        return video.kind === 'catwalk'
      }) > -1
    )
  }, [videos])

  const fitModelsAvailable =
    fitModel && featuredProduct.fitModelCount !== undefined && featuredProduct.fitModelCount > 1

  const colorCount = productGroup?.colorGroups.reduce((sum, group) => {
    return sum + group.colors?.length
  }, 0)

  const colorGalleryDecision = useFeatureFlag<'color-gallery-control' | 'color-gallery-variant'>({
    key: 'color-gallery-flag',
    controlVariant: 'color-gallery-control',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: colorCount <= 3,
  })
  const showColorGallery = colorGalleryDecision === 'color-gallery-variant'

  const fitModelCaption = useMemo(() => {
    const FitModelCaptionWrapper =
      showVideoCta || showColorGallery
        ? Fragment
        : ({ children }: { children: React.ReactNode }) => {
            return (
              <SliderFitModelWrapper>
                <StickyParent>{children}</StickyParent>
              </SliderFitModelWrapper>
            )
          }

    if (fitModelsAvailable || showVideoCta || showColorGallery) {
      return (
        <FitModelCaptionWrapper>
          <FitModelCaption
            colorCount={colorCount}
            activeFitModel={fitModel}
            productGroupHandle={productGroup.handle}
            activeProduct={featuredProduct}
            selectFitModel={x => setFitModel(x)}
            showVideoCta={showVideoCta}
            showColorGalleryCta={showColorGallery}
          />
        </FitModelCaptionWrapper>
      )
    }

    return null
  }, [
    showVideoCta,
    showColorGallery,
    fitModelsAvailable,
    colorCount,
    fitModel,
    productGroup.handle,
    featuredProduct,
    setFitModel,
  ])

  return (
    <GalleryImageStateContainer.Provider
      initialState={{
        activeFitModel: fitModel,
        detailImages: featuredProduct.detailImages,
        images,
        videos,
      }}
    >
      <ImageZoom />
      <ProductAssetsSection
        fitModelCaption={fitModelCaption}
        iconBadge={iconBadge}
        iconBadgePlacement={iconBadgePlacement}
        title={productGroup.title}
        productGroupStyle={productGroup.style}
      />
      {fitModelsAvailable && !showVideoCta && !showColorGallery && (
        <Media lessThan='md'>
          <FitModelCaption
            colorCount={colorCount}
            activeFitModel={fitModel}
            productGroupHandle={productGroup.handle}
            activeProduct={featuredProduct}
            selectFitModel={x => setFitModel(x)}
            showVideoCta={false}
            showColorGalleryCta={false}
          />
        </Media>
      )}
    </GalleryImageStateContainer.Provider>
  )
}

const ProductPageProductDetails: FC<ProductPageTemplateProps> = deprecatedProps => {
  const { data } = usePdpServerSideProps()
  if (!data) return null

  return (
    <>
      <ReviewSection product={data.product} variantGroup={data.product.variantGroup} />
      <DetailsSection product={data.product} deprecatedProps={deprecatedProps} />
    </>
  )
}

const ProductPageProductSection: FC<ProductPageTemplateProps> = props => {
  const { productGroup, productGroupStatus, featuredProduct, featuredProductStatus } = props

  return (
    <FitModelStateContainer.Provider
      initialState={{
        fitModel: featuredProduct.fitModel ?? undefined,
      }}
    >
      <ProductPageBreadcrumbs
        genderCategory={productGroup.genderCategory}
        category={productGroup.rawCategory}
      />
      <ProductPageStyles.PrimaryAndAsideSection
        fadeOut={productGroupStatus === 'pending' || featuredProductStatus === 'pending'}
      >
        <ProductPageStyles.PrimaryColumnDiv>
          <ProductPageProductGallery {...props} />
        </ProductPageStyles.PrimaryColumnDiv>

        <ProductPageStyles.AsideColumnDiv>
          <ProductPageProductDetails {...props} />
        </ProductPageStyles.AsideColumnDiv>
      </ProductPageStyles.PrimaryAndAsideSection>
    </FitModelStateContainer.Provider>
  )
}

const ProductPageContentfulSections = () => {
  const { data } = usePdpServerSideProps()
  if (!data) return null

  return (
    <>
      {data.productView?.sections.map((productGroupViewSectionModel, index) => {
        return (
          <PageSection
            key={`${productGroupViewSectionModel.id}-${index}`}
            sectionModel={productGroupViewSectionModel}
            index={index + 1}
          />
        )
      })}
    </>
  )
}

const ProductPageFigsScrubsAreSection = () => {
  const { data } = usePdpServerSideProps()
  if (!data) return null

  const isGiftCard = data.product.variantGroup.primaryCategory.key === 'GIFT_CARD'
  const isOuterLayerGarment = outerLayerGarmentCategoryKeys.includes(
    data.product.variantGroup.primaryCategory.key
  )
  if (!isGiftCard && !isOuterLayerGarment) {
    return null
  }

  return (
    <RenderOnceInViewport offset={{ bottom: 200 }}>
      <section>
        <FigsScrubsAreSection />
      </section>
    </RenderOnceInViewport>
  )
}

const ProductPageReviewsSection = () => {
  const { data } = usePdpServerSideProps()
  if (!data) return null

  const isGiftCard = isGiftCardHandle(data.product.handle)
  if (isGiftCard) return null

  return (
    <ReviewsPlaceholder>
      <RenderOnceInViewport offset={{ bottom: 200 }}>
        <section>
          <ReviewsBody
            handle={data.product.handle}
            shopifyProductId={data?.product.variantGroup.defaultVariant.shopifyProductId}
            colorKey={data.product.variantGroup.color?.id}
          />
        </section>
      </RenderOnceInViewport>
    </ReviewsPlaceholder>
  )
}

const ProductPageRecommendationsSections = (deprecatedProps: ProductPageTemplateProps) => {
  // DANGER & TODO: We don't want to display the recommendations section for portal users on PDP's that contain an active portal product.
  // Considering we don't know if a user is part of a portal on the server, this component will rendered during SSR and then removed
  // on the client after we've identified the user is part of a portal & that portal-specific color groups were returned for the product.
  const hideRecsSections = Boolean(
    deprecatedProps.productGroup.portalColorGroups &&
      deprecatedProps.productGroup.portalColorGroups.length > 0
  )

  const { data } = usePdpServerSideProps()
  const {
    pages: {
      product: { productRecommendations },
    },
  } = useTranslation()

  const removeWearItWithFlag = useFeatureFlag<'true' | 'false'>({
    key: 'remove-wear-it-with',
    controlVariant: 'false',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  if (hideRecsSections) return null
  if (!data?.product.variantGroup.color?.id) return null

  const hideContentfulManualRecs = removeWearItWithFlag === null || removeWearItWithFlag === 'true'
  const hideNostoAutomaticRecs = isGiftCardHandle(data.product.handle)

  return (
    <RenderOnceInViewport offset={{ bottom: 200 }}>
      <ProductPageStyles.Recommendations data-testid='product-recommendations'>
        {!hideContentfulManualRecs ? (
          <ShopTheLookSection
            handle={data.product.handle}
            isKit={false}
            colorKey={data.product.variantGroup.color.id}
            wrapper={<section data-testid='shop-the-look' />}
          />
        ) : null}
        {!hideNostoAutomaticRecs ? (
          <NostoProductRecommendationsSection
            analyticsContext={{ category: 'product-carousel-tile::productpage-nosto-1' }}
            nostoPlacementId='productpage-nosto-1'
            title={nostoTitle => nostoTitle ?? productRecommendations.title}
            wrapper={<div data-testid='nosto-product-recos' />}
          />
        ) : null}
      </ProductPageStyles.Recommendations>
    </RenderOnceInViewport>
  )
}

const ProductPageWhyWeLoveSection = (props: ProductPageTemplateProps) => {
  const { productGroup, featuredProduct } = props
  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup.handle,
  })
  const wwltImage = featuredProduct?.images?.nodes.find(image => image.kind === 'wwlt')

  if (isGiftCard) return null

  return (
    <Media greaterThanOrEqual='md'>
      <section>
        <WhyWeLove
          imageSrc={wwltImage?.source}
          whyWeLoveText={featuredProduct.whyWeLove}
          whyWeLoveIcons={featuredProduct.iconList}
        />
      </section>
    </Media>
  )
}

// Note: Don't access hooks in here. We don't want to add hook dependencies
// at such a high level that they render this entire page when their data changes.
// Really we want to avoid hooks pretty much at all in this file, and push them down
// towards leaf node components to minimize their impact on performance.
export const ProductPageTemplate = (props: ProductPageTemplateProps) => {
  return (
    <ProductPageStyles.Main>
      <ProductPageProductSection {...props} />
      <ProductPageContentfulSections />
      <ProductPageWhyWeLoveSection {...props} />
      <ProductPageRecommendationsSections {...props} />
      <ProductPageReviewsSection />
      <ProductPageFigsScrubsAreSection />
      <ProductPageTrackingEffects />
      <ProductPageSchemaTags />
    </ProductPageStyles.Main>
  )
}
